/**
 * Las DOS reglas de contraseña que no son de composición, en un único sitio.
 *
 * `passwordValidation` (common.ts) se describe a sí mismo como «canonical
 * enforced policy, unified for all environments», y la lista de requisitos del
 * registro decía reflejarlo «exactamente». Ninguna de las dos cosas era cierta:
 * el registro pasa además por `password-validator.service.ts`, que rechaza
 * secuencias obvias y contraseñas del listado común. El resultado medido en
 * e2e (build 0106cf6) era que el formulario marcaba los tres requisitos en
 * verde y anunciaba «Password meets all requirements», y el servidor devolvía
 * 400 WEAK_PASSWORD citando una cuarta regla que nunca se había enseñado:
 *
 *     'Sa2Probe123!'  → 3/3 ✓ en pantalla → 400 «contains common patterns»
 *
 * Por eso los predicados viven aquí y no en el backend: la pantalla que pide
 * la contraseña y la puerta que la juzga tienen que leer el MISMO código, o
 * vuelven a separarse en cuanto uno de los dos cambie.
 */

/**
 * Contraseñas notoriamente débiles. Se compara en minúsculas, así que las
 * entradas van en minúsculas.
 */
const COMMON_PASSWORDS: ReadonlySet<string> = new Set([
  // Top 100 most common passwords
  'password',
  '123456',
  '12345678',
  'qwerty',
  'abc123',
  'monkey',
  '1234567',
  'letmein',
  'trustno1',
  'dragon',
  'baseball',
  '111111',
  'iloveyou',
  'master',
  'sunshine',
  'ashley',
  'bailey',
  'passw0rd',
  'shadow',
  '123123',
  '654321',
  'superman',
  'qazwsx',
  'michael',
  'football',
  'jennifer',
  'killer',
  'harley',
  'batman',
  'andrew',
  'tigger',
  'password1',
  'admin',
  'welcome',
  'login',
  'princess',
  'solo',
  'azerty',
  'password123',
  '123456789',
  '1234567890',
  'qwertyuiop',
  'myspace1',
  'password2',
  '1q2w3e4r',
  'qwerty123',
  '123qwe',
  '1qaz2wsx',
  'qwerty1',
  'baseball1',
  'football1',
  // Common patterns
  'aaaaaaaa',
  'abcdefgh',
  'abcd1234',
  '1234abcd',
  'aaaa1111',
  'password!',
  'password12',
  'password12!',
  // Contextual passwords
  'blockandtaxes',
  'blocktaxes',
  'bat2024',
  'bat2025',
  // Additional common passwords with numbers and symbols
  'password1!',
  'password123!',
  'passw0rd!',
  'qwerty123!',
])

const SEQUENTIAL_LETTERS =
  /(abc|bcd|cde|def|efg|fgh|ghi|hij|ijk|jkl|klm|lmn|mno|nop|opq|pqr|qrs|rst|stu|tuv|uvw|vwx|wxy|xyz)/i
const SEQUENTIAL_DIGITS = /(012|123|234|345|456|567|678|789|890)/
const SEQUENTIAL_KEYBOARD =
  /(qwe|wer|ert|rty|tyu|yui|iop|asd|sdf|dfg|fgh|ghj|hjk|jkl|zxc|xcv|cvb|vbn|bnm)/i

/**
 * Cinco o más apariciones seguidas del MISMO carácter («aaaaa», «11111»).
 *
 * ⚠️ El `\1` es una retrorreferencia, no el dígito uno. Estuvo escrito
 * `/(.)1{4,}/`, que no es un error de estilo: exige un carácter cualquiera
 * seguido de cuatro «1» literales, así que la regla sólo saltaba con rachas
 * del dígito 1 y NINGÚN otro carácter repetido la disparaba. Medido contra la
 * API de e2e antes de arreglarlo: `aaaaaaaa1` y `bbbbbbbb2` devolvían
 * 201 CREATED, y sólo `11111111a` se rechazaba — por casualidad.
 */
const REPEATED_CHARACTER = /(.)\1{4,}/

/** Filas completas del teclado. */
const KEYBOARD_ROW = /(?:qwerty|asdfgh|zxcvbn)/i

/**
 * ¿La contraseña contiene una secuencia obvia (letras, dígitos o teclado), una
 * fila entera del teclado, o un carácter repetido cinco veces o más?
 */
export function hasObviousPasswordPattern(password: string): boolean {
  return (
    SEQUENTIAL_LETTERS.test(password) ||
    SEQUENTIAL_DIGITS.test(password) ||
    SEQUENTIAL_KEYBOARD.test(password) ||
    REPEATED_CHARACTER.test(password) ||
    KEYBOARD_ROW.test(password)
  )
}

/** ¿Es una de las contraseñas notoriamente comunes? (sin distinguir mayúsculas) */
export function isCommonPassword(password: string): boolean {
  return COMMON_PASSWORDS.has(password.toLowerCase())
}

/**
 * Las dos reglas anteriores juntas: lo que el formulario tiene que poder
 * enseñar como un requisito más, porque el servidor las aplica.
 */
export function isPredictablePassword(password: string): boolean {
  return isCommonPassword(password) || hasObviousPasswordPattern(password)
}
